/**
 * Self-service CV import — reuses pool import progress UI (4-step rail).
 */
import { useState } from 'react';
import { ImportProgressPanel } from '../CandidatePools/ImportProgressPanel';
import { CvImportUploadZone } from '../CandidatePools/CvImportUploadZone';
import type { UsePersonCvUploadResult } from '../../hooks/usePersonCvUpload';
import { profileUpdateApi } from '../../services/profileUpdateApi';

interface Props {
  cvUpload: UsePersonCvUploadResult;
  /** Magic-link token for paste (file upload uses cvUpload hook). */
  token?: string | null;
  jwt: string;
  onImported: () => void;
}

export function CvImportCard({ cvUpload, token, jwt, onImported }: Props) {
  const [mode, setMode] = useState<'drop' | 'paste'>('drop');
  const [pasteText, setPasteText] = useState('');
  const [pasteBusy, setPasteBusy] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);

  const runPaste = async () => {
    const trimmed = pasteText.trim();
    if (!trimmed) {
      setPasteError('Paste your CV text first.');
      return;
    }
    setPasteError(null);
    setPasteBusy(true);
    try {
      await profileUpdateApi.pasteCv(trimmed, token, jwt);
      setPasteText('');
      onImported();
    } catch (e: unknown) {
      const resp = (e as { response?: { data?: { error?: string } } }).response;
      setPasteError(resp?.data?.error || 'Failed to import CV');
    } finally {
      setPasteBusy(false);
    }
  };

  const fileBusy =
    cvUpload.busy ||
    cvUpload.importState.phase === 'uploading' ||
    cvUpload.importState.phase === 'parsing' ||
    cvUpload.importState.phase === 'saving';

  return (
    <section
      aria-label="Import profile from CV"
      className="rounded-xl bg-[#0a0a0a] border border-[#2a2a2a] p-5 mb-6"
      data-testid="cv-import-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-bold text-[#e5e5e5]">Update from CV</h2>
          <p className="text-xs text-[#a3a3a3]">
            Same import as talent pools — LLM parse with live progress.
          </p>
        </div>
        <div className="flex rounded-lg overflow-hidden border border-[#2a2a2a] text-xs">
          <button
            type="button"
            onClick={() => setMode('drop')}
            className={`px-3 py-1 ${mode === 'drop' ? 'bg-[#4A2CFF] text-white' : 'text-[#a3a3a3]'}`}
            aria-pressed={mode === 'drop'}
          >
            Upload file
          </button>
          <button
            type="button"
            onClick={() => setMode('paste')}
            className={`px-3 py-1 ${mode === 'paste' ? 'bg-[#4A2CFF] text-white' : 'text-[#a3a3a3]'}`}
            aria-pressed={mode === 'paste'}
          >
            Paste text
          </button>
        </div>
      </div>

      {mode === 'drop' ? (
        <CvImportUploadZone
          cvUpload={cvUpload}
          disabled={pasteBusy}
          idleLabel="Drag & drop or click to upload your CV"
        />
      ) : (
        <div>
          <textarea
            aria-label="Paste CV text"
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            rows={6}
            disabled={pasteBusy || fileBusy}
            placeholder="Paste your CV text here…"
            className="w-full px-3 py-2 rounded-lg bg-[#1a1a1a] border border-[#2a2a2a] text-[#e5e5e5] text-sm placeholder:text-[#6b7280] disabled:opacity-60"
          />
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={runPaste}
              disabled={pasteBusy || fileBusy || !pasteText.trim()}
              className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-[#4A2CFF] text-white disabled:opacity-50"
            >
              {pasteBusy ? 'Importing…' : 'Import from text'}
            </button>
          </div>
        </div>
      )}

      <ImportProgressPanel
        state={cvUpload.importState}
        onCancel={cvUpload.cancelImport}
        onDismiss={cvUpload.resetImport}
        onRetry={cvUpload.retryUpload}
        context="profile"
      />

      {pasteError && (
        <p className="mt-3 text-xs text-red-400" role="alert">
          {pasteError}
        </p>
      )}
    </section>
  );
}
